import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Menu,
  MenuItem,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import DateRangeOutlinedIcon from '@mui/icons-material/DateRangeOutlined';
import PlayArrowOutlinedIcon from '@mui/icons-material/PlayArrowOutlined';
import { LockOutlined as LockOutlinedIcon } from '@mui/icons-material';
import { useProductosMaterialContext } from 'context';
import { useApi } from 'lib/api';
import {
  PROGRAMA_CATALOGOS_NOMINA,
  PROGRAMA_NOMINA,
  esRolAdministrador,
  puedeAccionPrograma,
  puedeEditarProgramaNomina,
  puedeVerModuloNomina,
} from 'lib/seguridad/permisosPorModulo';
import { FP, PageHeader, fpDataGridSx, fpInputSx } from 'components/financepro';
import IniciarNomina, { puedeUsarIniciarNomina as puedeVerAccionIniciarNomina } from './IniciarNomina';
import DefinicionNomina, {
  puedeUsarDefinicionNomina as puedeVerAccionDefinicionNomina,
} from './DefinicionNomina';
import CerrarNomina, {
  puedeUsarCerrarNomina as puedeVerAccionCerrarNomina,
} from './CerrarNomina';

const btnBase = { textTransform: 'none', borderRadius: 1.8, fontWeight: 700, boxShadow: 'none' };
const btnOutline = (c) => ({
  ...btnBase,
  color: c,
  bgcolor: '#FFFFFF',
  border: `1px solid ${c}`,
  '&:hover': { bgcolor: `${c}12`, border: `1px solid ${c}`, color: c },
  '&.Mui-disabled': { color: FP.color.textMuted, borderColor: FP.color.border },
});
const btnSolid = (bg, hover) => ({
  ...btnBase,
  bgcolor: bg,
  color: '#FFFFFF',
  '&:hover': { bgcolor: hover, boxShadow: 'none', color: '#FFFFFF' },
  '&.Mui-disabled': { bgcolor: `${bg}66`, color: '#FFFFFF' },
});

const sectionBox = {
  border: `1px solid ${FP.color.border}`,
  borderRadius: 2.5,
  p: { xs: 2, sm: 2.6 },
  mb: 0,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
};

const cardShellSx = {
  borderRadius: 2.5,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
  border: `1px solid ${FP.color.border}`,
  overflow: 'hidden',
};

const formularioDeptoVacio = { id: '', descrip: '', planta: '' };
const formularioSectorVacio = { id: '', descrip: '' };

function NoRowsOverlay() {
  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      sx={{ height: '100%', py: 6, color: FP.color.textMuted }}
    >
      <InboxOutlinedIcon sx={{ fontSize: 44, mb: 1 }} />
      <Typography sx={{ fontWeight: 700, color: FP.color.textSecondary }}>Sin registros</Typography>
    </Stack>
  );
}

/**
 * Departamentos (fps1020) y sectores (fps1030) en una sola opción de menú.
 * Lectura: igual que InicioNomina (`puedeVerModuloNomina`).
 * Edición: programa `catalogosnomina`, umbrella `nomina` o administrador.
 */
const CatalogosNomina = () => {
  const { controller, selectedCompany, showSuccess, showError } = useProductosMaterialContext();
  const { usuarioLog, roleDetalles, roleWeb, nombreNegocio } = controller;
  const { post } = useApi();

  const puedeLeer = useMemo(
    () =>
      puedeVerModuloNomina(roleDetalles, roleWeb) ||
      puedeEditarProgramaNomina(roleDetalles, PROGRAMA_CATALOGOS_NOMINA, roleWeb),
    [roleDetalles, roleWeb]
  );
  const puedeModificar = useMemo(
    () => puedeEditarProgramaNomina(roleDetalles, PROGRAMA_CATALOGOS_NOMINA, roleWeb),
    [roleDetalles, roleWeb]
  );
  const puedeEliminar = useMemo(
    () =>
      esRolAdministrador(roleWeb) ||
      puedeAccionPrograma(roleDetalles, PROGRAMA_CATALOGOS_NOMINA, 'borrar') ||
      puedeAccionPrograma(roleDetalles, PROGRAMA_NOMINA, 'borrar'),
    [roleDetalles, roleWeb]
  );
  const puedeIniciarNomina = useMemo(
    () => puedeVerAccionIniciarNomina(roleDetalles),
    [roleDetalles]
  );
  const puedeDefinirNomina = useMemo(
    () => puedeVerAccionDefinicionNomina(roleDetalles),
    [roleDetalles]
  );
  const puedeCerrarNomina = useMemo(
    () => puedeVerAccionCerrarNomina(roleDetalles),
    [roleDetalles]
  );

  const [pestana, setPestana] = useState('departamentos');
  const [departamentos, setDepartamentos] = useState([]);
  const [sectores, setSectores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editando, setEditando] = useState(null);
  const [formDepto, setFormDepto] = useState(formularioDeptoVacio);
  const [formSector, setFormSector] = useState(formularioSectorVacio);
  const [guardando, setGuardando] = useState(false);
  const [eliminando, setEliminando] = useState(null);
  const [borrando, setBorrando] = useState(false);
  const [menuAcciones, setMenuAcciones] = useState(null);
  const [accionActiva, setAccionActiva] = useState(null);

  const opcionesAcciones = useMemo(
    () =>
      [
        {
          id: 'iniciarnomina',
          label: 'Inicio Nómina',
          icon: <PlayArrowOutlinedIcon fontSize="small" />,
          visible: puedeIniciarNomina,
        },
        {
          id: 'definicionnomina',
          label: 'Definición de nómina',
          icon: <DateRangeOutlinedIcon fontSize="small" />,
          visible: puedeDefinirNomina,
        },
        {
          id: 'cerrarnomina',
          label: 'Cerrar nómina',
          icon: <LockOutlinedIcon fontSize="small" />,
          visible: puedeCerrarNomina,
        },
      ].filter((op) => op.visible),
    [puedeIniciarNomina, puedeDefinirNomina, puedeCerrarNomina]
  );

  const puedeOperar = Boolean(selectedCompany?.db_empresa && usuarioLog);
  const esDepto = pestana === 'departamentos';
  const catalogo = esDepto
    ? { etiqueta: 'Departamentos', singular: 'departamento' }
    : { etiqueta: 'Sectores', singular: 'sector' };

  const credencialesEmpresa = useCallback(
    () => ({ id_empresa: selectedCompany }),
    [selectedCompany]
  );

  const cargarDepartamentos = useCallback(async () => {
    const { ok, data } = await post('/api/nomina_listardepartamentos', credencialesEmpresa());
    if (!ok) throw new Error(data.Error || 'Error al cargar departamentos');
    setDepartamentos(Array.isArray(data.departamentos) ? data.departamentos : []);
  }, [post, credencialesEmpresa]);

  const cargarSectores = useCallback(async () => {
    const { ok, data } = await post('/api/nomina_listarsectores', credencialesEmpresa());
    if (!ok) throw new Error(data.Error || 'Error al cargar sectores');
    setSectores(Array.isArray(data.sectores) ? data.sectores : []);
  }, [post, credencialesEmpresa]);

  const cargar = useCallback(async () => {
    if (!puedeLeer || !puedeOperar) {
      setDepartamentos([]);
      setSectores([]);
      return;
    }
    setLoading(true);
    try {
      await Promise.all([cargarDepartamentos(), cargarSectores()]);
    } catch (e) {
      showError(e.message || 'Error al cargar los catálogos de nómina');
    } finally {
      setLoading(false);
    }
  }, [puedeLeer, puedeOperar, cargarDepartamentos, cargarSectores, showError]);

  useEffect(() => {
    if (puedeLeer && puedeOperar) cargar();
  }, [puedeLeer, puedeOperar, cargar]);

  const abrirNuevo = () => {
    if (!puedeModificar) {
      showError(`No tiene permisos para crear ${catalogo.singular}s`);
      return;
    }
    if (esDepto) setFormDepto(formularioDeptoVacio);
    else setFormSector(formularioSectorVacio);
    setEditando(0);
  };

  const abrirEditar = (fila) => {
    if (!puedeModificar) {
      showError(`No tiene permisos para modificar ${catalogo.singular}s`);
      return;
    }
    if (esDepto) {
      setFormDepto({
        id: String(fila.id ?? ''),
        descrip: fila.descrip || '',
        planta: fila.planta || '',
      });
    } else {
      setFormSector({ id: String(fila.id ?? ''), descrip: fila.descrip || '' });
    }
    setEditando(fila.id);
  };

  const cerrarDialogo = () => {
    if (guardando) return;
    setEditando(null);
    setFormDepto(formularioDeptoVacio);
    setFormSector(formularioSectorVacio);
  };

  const guardar = async () => {
    if (!puedeModificar) {
      showError(`No tiene permisos para guardar ${catalogo.singular}s`);
      return;
    }
    if (esDepto) {
      if (!String(formDepto.id).trim()) {
        showError('El departamento no puede estar en blanco');
        return;
      }
      if (!String(formDepto.descrip).trim()) {
        showError('La descripción no puede estar en blanco');
        return;
      }
      if (!String(formDepto.planta).trim()) {
        showError('La planta no puede estar en blanco');
        return;
      }
      setGuardando(true);
      try {
        const { ok, data } = await post('/api/nomina_guardardepartamento', {
          ...credencialesEmpresa(),
          id: formDepto.id,
          descrip: formDepto.descrip,
          planta: formDepto.planta,
        });
        if (!ok) throw new Error(data.Error || 'No se pueden grabar los datos');
        showSuccess(data.ok || 'Datos guardados');
        cerrarDialogo();
        await cargarDepartamentos();
      } catch (e) {
        showError(e.message || 'No se pueden grabar los datos');
      } finally {
        setGuardando(false);
      }
      return;
    }

    if (!String(formSector.id).trim()) {
      showError('El sector no puede estar en blanco');
      return;
    }
    if (!String(formSector.descrip).trim()) {
      showError('La descripción no puede estar en blanco');
      return;
    }
    setGuardando(true);
    try {
      const { ok, data } = await post('/api/nomina_guardarsector', {
        ...credencialesEmpresa(),
        id: formSector.id,
        descrip: formSector.descrip,
      });
      if (!ok) throw new Error(data.Error || 'No se pueden grabar los datos');
      showSuccess(data.ok || 'Datos guardados');
      cerrarDialogo();
      await cargarSectores();
    } catch (e) {
      showError(e.message || 'No se pueden grabar los datos');
    } finally {
      setGuardando(false);
    }
  };

  const confirmarEliminar = async () => {
    if (!eliminando) return;
    if (!puedeEliminar) {
      showError(`No tiene permisos para eliminar ${catalogo.singular}s`);
      return;
    }
    setBorrando(true);
    try {
      const ruta = esDepto ? '/api/nomina_eliminardepartamento' : '/api/nomina_eliminarsector';
      const { ok, data } = await post(ruta, { ...credencialesEmpresa(), id: eliminando.id });
      if (!ok) throw new Error(data.Error || 'No se pudo eliminar el registro');
      showSuccess(data.ok || 'Registro borrado');
      setEliminando(null);
      if (esDepto) await cargarDepartamentos();
      else await cargarSectores();
    } catch (e) {
      showError(e.message || 'No se pudo eliminar el registro');
    } finally {
      setBorrando(false);
    }
  };

  const rows = esDepto ? departamentos : sectores;

  const colAcciones = {
    field: 'acciones',
    headerName: '',
    width: puedeEliminar ? 210 : 110,
    sortable: false,
    filterable: false,
    renderCell: (params) => (
      <Stack direction="row" spacing={1}>
        <Button
          size="small"
          startIcon={<EditOutlinedIcon />}
          onClick={() => abrirEditar(params.row)}
          disabled={!puedeModificar}
          sx={btnOutline(FP.color.navy)}
        >
          Editar
        </Button>
        {puedeEliminar && (
          <Button
            size="small"
            startIcon={<DeleteOutlineIcon />}
            onClick={() => setEliminando(params.row)}
            sx={btnOutline('#B3261E')}
          >
            Eliminar
          </Button>
        )}
      </Stack>
    ),
  };

  const columns = esDepto
    ? [
        { field: 'id', headerName: 'Departamento', width: 140 },
        { field: 'descrip', headerName: 'Descripción', flex: 1, minWidth: 200 },
        { field: 'planta', headerName: 'Planta', width: 90 },
        { field: 'supervisor_label', headerName: 'Supervisor', flex: 1, minWidth: 180 },
        colAcciones,
      ]
    : [
        { field: 'id', headerName: 'Sector', width: 140 },
        { field: 'descrip', headerName: 'Descripción', flex: 1, minWidth: 220 },
        colAcciones,
      ];

  const renderAcceso = (mensaje) => (
    <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface }}>
      <PageHeader
        title="Departamentos y sectores"
        subtitle={nombreNegocio ? `${nombreNegocio} · Nómina` : 'Nómina'}
      />
      <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 }, display: 'flex', justifyContent: 'center' }}>
        <Card
          elevation={0}
          sx={{ ...cardShellSx, maxWidth: 560, width: '100%', textAlign: 'center', p: { xs: 3, md: 4 } }}
        >
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: 1.6,
              mx: 'auto',
              mb: 2.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: FP.color.dangerSoft,
              color: FP.color.danger,
            }}
          >
            <LockOutlinedIcon sx={{ fontSize: 30 }} />
          </Box>
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: '1.25rem',
              color: FP.color.textPrimary,
              mb: 1,
              letterSpacing: '-0.01em',
            }}
          >
            Acceso denegado
          </Typography>
          <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.95rem', mb: 1.2 }}>
            {mensaje}
          </Typography>
          <Typography sx={{ color: FP.color.textMuted, fontSize: '0.85rem' }}>
            Por favor, contacte al administrador del sistema para solicitar los permisos necesarios.
          </Typography>
        </Card>
      </Box>
    </Box>
  );

  if (!puedeLeer) {
    return renderAcceso('No tienes permisos para acceder al módulo de nómina.');
  }
  if (!puedeOperar) {
    return renderAcceso('Seleccione una empresa para administrar los catálogos de nómina.');
  }

  return (
    <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface }}>
      <PageHeader
        title="Departamentos y sectores"
        subtitle={nombreNegocio ? `${nombreNegocio} · Nómina` : 'Nómina'}
        cta={
          <Stack direction="row" spacing={1}>
            <Button
              startIcon={<AddIcon />}
              onClick={abrirNuevo}
              disabled={!puedeModificar}
              sx={btnOutline(FP.color.navy)}
            >
              Nuevo {catalogo.singular}
            </Button>
            <Button
              endIcon={<KeyboardArrowDownIcon />}
              onClick={(e) => setMenuAcciones(e.currentTarget)}
              sx={btnOutline(FP.color.navy)}
            >
              Acciones
            </Button>
            <Menu
              anchorEl={menuAcciones}
              open={Boolean(menuAcciones)}
              onClose={() => setMenuAcciones(null)}
              PaperProps={{
                sx: {
                  mt: 1,
                  minWidth: 200,
                  borderRadius: 2,
                  border: `1px solid ${FP.color.border}`,
                  boxShadow: FP.shadow.soft,
                },
              }}
            >
              {opcionesAcciones.length === 0 ? (
                <MenuItem disabled sx={{ fontWeight: 600, color: FP.color.textMuted }}>
                  No hay acciones disponibles
                </MenuItem>
              ) : (
                opcionesAcciones.map((op) => (
                  <MenuItem
                    key={op.id}
                    onClick={() => {
                      setMenuAcciones(null);
                      setAccionActiva(op.id);
                    }}
                    sx={{ fontWeight: 600, color: FP.color.textPrimary, gap: 1 }}
                  >
                    {op.icon}
                    {op.label}
                  </MenuItem>
                ))
              )}
            </Menu>
          </Stack>
        }
      />
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Box sx={sectionBox}>
          <Grid container spacing={2} sx={{ mb: 1.5 }} alignItems="center">
            <Grid item xs={12} sm>
              <Tabs
                value={pestana}
                onChange={(_, v) => {
                  setPestana(v);
                  setEditando(null);
                  setEliminando(null);
                }}
                sx={{
                  minHeight: 40,
                  bgcolor: 'transparent',
                  p: 0,
                  borderRadius: 0,
                  '& .MuiTab-root': {
                    textTransform: 'none',
                    fontWeight: 700,
                    minHeight: 40,
                    color: FP.color.textSecondary,
                    opacity: 1,
                  },
                  '& .MuiTab-root.Mui-selected': { color: FP.color.navy },
                  '& .MuiTabs-indicator': {
                    backgroundColor: FP.color.navy,
                    height: 3,
                    borderRadius: '3px 3px 0 0',
                    boxShadow: 'none',
                  },
                }}
              >
                <Tab value="departamentos" label="Departamentos" />
                <Tab value="sectores" label="Sectores" />
              </Tabs>
            </Grid>
            <Grid item xs={12} sm="auto" sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Typography sx={{ fontWeight: 700, color: FP.color.textSecondary, fontSize: '0.85rem' }}>
                {rows.length} registros
              </Typography>
            </Grid>
          </Grid>
          <Box sx={{ bgcolor: '#fff' }}>
            <DataGrid
              autoHeight
              rows={rows}
              columns={columns}
              loading={loading}
              getRowId={(r) => r.id}
              rowHeight={46}
              pageSizeOptions={[25, 50]}
              initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
              slots={{ noRowsOverlay: NoRowsOverlay }}
              sx={{ ...fpDataGridSx, '--DataGrid-overlayHeight': '220px' }}
              disableRowSelectionOnClick
            />
          </Box>
        </Box>
      </Box>

      <Dialog
        open={editando !== null}
        onClose={cerrarDialogo}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 800 }}>
          {editando === 0 ? `Nuevo ${catalogo.singular}` : `Editar ${catalogo.singular}`}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            {esDepto ? (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField
                    required
                    fullWidth
                    label="Departamento"
                    value={formDepto.id}
                    onChange={(e) =>
                      setFormDepto((p) => ({ ...p, id: e.target.value.replace(/\D/g, '') }))
                    }
                    disabled={editando !== 0}
                    inputProps={{ maxLength: 10, inputMode: 'numeric' }}
                    helperText="Código numérico"
                    InputLabelProps={{ shrink: true }}
                    sx={fpInputSx}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    required
                    fullWidth
                    label="Planta"
                    value={formDepto.planta}
                    onChange={(e) =>
                      setFormDepto((p) => ({
                        ...p,
                        planta: e.target.value.replace(/\D/g, '').slice(0, 2),
                      }))
                    }
                    inputProps={{ maxLength: 2, inputMode: 'numeric' }}
                    helperText="1 o 2 dígitos"
                    InputLabelProps={{ shrink: true }}
                    sx={fpInputSx}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    required
                    fullWidth
                    label="Descripción"
                    value={formDepto.descrip}
                    onChange={(e) => setFormDepto((p) => ({ ...p, descrip: e.target.value }))}
                    inputProps={{ maxLength: 40 }}
                    helperText="Hasta 40 caracteres"
                    InputLabelProps={{ shrink: true }}
                    sx={fpInputSx}
                  />
                </Grid>
              </>
            ) : (
              <>
                <Grid item xs={12} sm={4}>
                  <TextField
                    required
                    fullWidth
                    label="Sector"
                    value={formSector.id}
                    onChange={(e) =>
                      setFormSector((p) => ({ ...p, id: e.target.value.replace(/\D/g, '') }))
                    }
                    disabled={editando !== 0}
                    inputProps={{ maxLength: 10, inputMode: 'numeric' }}
                    helperText="Código numérico"
                    InputLabelProps={{ shrink: true }}
                    sx={fpInputSx}
                  />
                </Grid>
                <Grid item xs={12} sm={8}>
                  <TextField
                    required
                    fullWidth
                    label="Descripción"
                    value={formSector.descrip}
                    onChange={(e) => setFormSector((p) => ({ ...p, descrip: e.target.value }))}
                    inputProps={{ maxLength: 40 }}
                    helperText="Hasta 40 caracteres"
                    InputLabelProps={{ shrink: true }}
                    sx={fpInputSx}
                  />
                </Grid>
              </>
            )}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={cerrarDialogo} disabled={guardando} sx={btnOutline(FP.color.textSecondary)}>
            Cancelar
          </Button>
          <Button
            onClick={guardar}
            disabled={guardando || !puedeModificar}
            startIcon={guardando ? <CircularProgress size={16} color="inherit" /> : null}
            sx={btnSolid(FP.color.navy, FP.color.navyDeep)}
          >
            {guardando ? 'Guardando…' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={eliminando != null}
        onClose={() => !borrando && setEliminando(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 800 }}>Eliminar {catalogo.singular}</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: FP.color.textPrimary, fontSize: '0.9rem' }}>
            ¿Confirma eliminar {eliminando?.descrip || 'este registro'}? Si está asignado a
            empleados, el sistema impedirá borrarlo.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setEliminando(null)}
            disabled={borrando}
            sx={btnOutline(FP.color.textSecondary)}
          >
            Cancelar
          </Button>
          <Button
            onClick={confirmarEliminar}
            disabled={borrando}
            startIcon={borrando ? <CircularProgress size={16} color="inherit" /> : <DeleteOutlineIcon />}
            sx={btnSolid('#B3261E', '#8E1B16')}
          >
            {borrando ? 'Eliminando…' : 'Eliminar'}
          </Button>
        </DialogActions>
      </Dialog>

      {puedeIniciarNomina && (
        <IniciarNomina
          open={accionActiva === 'iniciarnomina'}
          onClose={() => setAccionActiva(null)}
        />
      )}
      {puedeDefinirNomina && (
        <DefinicionNomina
          open={accionActiva === 'definicionnomina'}
          onClose={() => setAccionActiva(null)}
        />
      )}
      {puedeCerrarNomina && (
        <CerrarNomina
          open={accionActiva === 'cerrarnomina'}
          onClose={() => setAccionActiva(null)}
        />
      )}
    </Box>
  );
};

export default CatalogosNomina;
